import React, { useMemo } from 'react';
import { MapPinIcon } from '@heroicons/react/24/outline';
import type { FarmField } from './types';

interface Point {
    lat: number;
    lng: number;
}

export interface MiniMapMarker {
    kind?: 'start' | 'end' | 'obs' | 'field';
    label?: string;
    lat?: number;
    lng?: number;
    color?: string;
}

interface FieldMiniMapProps {
    field?: FarmField | null;
    markers?: MiniMapMarker[];
    height?: number;
}

const normalizeCoordinates = (raw: any): Point[] => {
    let coords = raw;
    if (typeof coords === 'string') {
        try {
            coords = JSON.parse(coords);
        } catch {
            return [];
        }
    }
    // GeoJSON Polygon => first ring
    if (coords && coords.type === 'Polygon' && Array.isArray(coords.coordinates)) {
        coords = coords.coordinates[0];
    }
    if (!Array.isArray(coords)) return [];
    const points = coords
        .map((c: any): Point | null => {
            if (Array.isArray(c) && c.length >= 2) {
                const lat = Number(c[0]);
                const lng = Number(c[1]);
                return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
            }
            const lat = Number(c?.lat ?? c?.latitude);
            const lng = Number(c?.lng ?? c?.longitude);
            return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
        })
        .filter((p: Point | null): p is Point => p !== null);
    // Drop duplicate closing point for GeoJSON rings
    if (points.length > 3) {
        const first = points[0];
        const last = points[points.length - 1];
        if (first.lat === last.lat && first.lng === last.lng) points.pop();
    }
    return points;
};

/**
 * Compact SVG field-boundary mini-map. Renders the plot polygon plus GPS
 * markers (start / completion / observation / field centre) without any
 * external tile dependency.
 */
const FieldMiniMap: React.FC<FieldMiniMapProps> = ({ field, markers = [], height = 180 }) => {
    const polygon = useMemo(() => (field ? normalizeCoordinates(field.boundary_coordinates) : []), [field]);

    const view = useMemo(() => {
        if (polygon.length < 3) return null;
        const lats = polygon.map((p) => p.lat);
        const lngs = polygon.map((p) => p.lng);
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const minLng = Math.min(...lngs);
        const maxLng = Math.max(...lngs);
        const spanLat = maxLat - minLat || 0.0001;
        const spanLng = maxLng - minLng || 0.0001;
        const pad = 0.2;
        const padLat = spanLat * pad;
        const padLng = spanLng * pad;
        return {
            minLat: minLat - padLat,
            maxLat: maxLat + padLat,
            minLng: minLng - padLng,
            maxLng: maxLng + padLng,
            spanLat: spanLat + 2 * padLat,
            spanLng: spanLng + 2 * padLng,
        };
    }, [polygon]);

    const project = (lat: number, lng: number): { x: number; y: number } | null => {
        if (!view) return null;
        const x = ((lng - view.minLng) / view.spanLng) * 100;
        const y = ((view.maxLat - lat) / view.spanLat) * 100;
        return { x, y };
    };

    const pointsAttr = useMemo(() => {
        if (!view) return '';
        return polygon.map((p) => project(p.lat, p.lng)).map((p) => `${p?.x.toFixed(2)},${p?.y.toFixed(2)}`).join(' ');
    }, [polygon, view]); // eslint-disable-line react-hooks/exhaustive-deps

    const visibleMarkers = markers.filter((m) => Number.isFinite(Number(m.lat)) && Number.isFinite(Number(m.lng)));

    if (!view || polygon.length < 3) {
        return (
            <div
                className="flex w-full items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50"
                style={{ height }}
            >
                <div className="text-center">
                    <MapPinIcon className="mx-auto h-6 w-6 text-slate-300" />
                    <p className="mt-1 text-xs font-bold text-slate-500">{field?.name || 'Field'}</p>
                    <p className="text-[11px] text-slate-400">No boundary mapped yet</p>
                </div>
            </div>
        );
    }

    return (
        <div className="relative w-full overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-br from-emerald-50 to-sky-50" style={{ height }}>
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="h-full w-full">
                <polygon
                    points={pointsAttr}
                    fill="rgba(16,185,129,0.15)"
                    stroke="#059669"
                    strokeWidth={1}
                    strokeLinejoin="round"
                />
                {visibleMarkers.map((marker, index) => {
                    const pos = project(Number(marker.lat), Number(marker.lng));
                    if (!pos) return null;
                    const colors = marker.color
                        ? { fill: marker.color, text: '#ffffff' }
                        : marker.kind === 'start'
                        ? { fill: '#059669', text: '#ffffff' }
                        : marker.kind === 'end'
                        ? { fill: '#dc2626', text: '#ffffff' }
                        : marker.kind === 'obs'
                        ? { fill: '#f59e0b', text: '#ffffff' }
                        : { fill: '#2563eb', text: '#ffffff' };
                    return (
                        <g key={`${marker.kind}-${index}`} transform={`translate(${pos.x} ${pos.y})`}>
                            <circle r={3} fill={colors.fill} stroke="#ffffff" strokeWidth={0.6} />
                            {marker.label && (
                                <text y={-3.2} textAnchor="middle" fontSize={2.8} fontWeight={700} fill={colors.fill}>
                                    {marker.label}
                                </text>
                            )}
                        </g>
                    );
                })}
            </svg>
            <div className="absolute bottom-1.5 left-2 rounded bg-white/85 px-2 py-0.5 text-[10px] font-bold text-slate-600 shadow-sm">
                {field?.name || 'Field'}
                {field?.plot_code ? ` · ${field.plot_code}` : ''}
            </div>
        </div>
    );
};

export default FieldMiniMap;
